import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const { login, loading, error } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await login(formData);
    if (success) {
      navigate('/chat');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <>
      {/* Google Fonts Import */}
      <link
        href="https://fonts.googleapis.com/css2?family=Lobster&display=swap"
        rel="stylesheet"
      />

      <div style={{
        minHeight: '100vh',
        background: theme === 'dark'
          ? '#1a1a1a'
          : 'rgba(177,187,214,0.60)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        position: 'relative'
      }}>
        {/* Theme Toggle Button */}
        <button
          onClick={toggleTheme}
          style={{
            position: 'absolute',
            top: '1.5rem',
            right: '1.5rem',
            background: 'none',
            border: 'none',
            borderRadius: '0.375rem',
            width: '40px',
            height: '40px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem',
            color: theme === 'dark' ? '#ffffff' : '#2d333a',
            transition: 'all 0.15s ease'
          }}
          title="Toggle theme"
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2a2a2a' : '#f5f5f5';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>

      <div style={{
        width: '100%',
        maxWidth: '450px'
      }}>
        <div style={{
          backgroundColor: theme === 'dark' ? '#2d333a' : '#ffffff',
          borderRadius: '1rem',
          boxShadow: theme === 'dark'
            ? '0 20px 40px rgba(0, 0, 0, 0.3)'
            : '0 20px 40px rgba(0, 0, 0, 0.1)',
          padding: '3rem',
          border: `1px solid ${theme === 'dark' ? '#404040' : 'rgba(39,71,151,0.2)'}`
        }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: 800,
              color: '#274797',
              marginBottom: '0.5rem',
              fontFamily: "'Lobster', cursive",
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}>
              SpaarxSense
            </h1>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: theme === 'dark' ? '#ffffff' : '#2d333a',
              marginBottom: '0.5rem'
            }}>
              Welcome Back
            </h2>
            <p style={{
              color: theme === 'dark' ? '#9ca3af' : '#6e7681',
              fontSize: '1rem'
            }}>
              Sign in to your account
            </p>
          </div>

          <form onSubmit={handleSubmit} style={{ marginBottom: '1.5rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                marginBottom: '0.5rem'
              }}>
                Email Address
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{
                  position: 'absolute',
                  left: '1rem',
                  color: '#8e9297',
                  fontSize: '0.875rem',
                  zIndex: 1
                }}>
                  ✉
                </span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter your email"
                  style={{
                    width: '100%',
                    padding: '1rem 1rem 1rem 2.5rem',
                    border: `1px solid ${theme === 'dark' ? '#404040' : '#e5e5e5'}`,
                    borderRadius: '0.5rem',
                    backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff',
                    color: theme === 'dark' ? '#ffffff' : '#2d333a',
                    fontSize: '1rem',
                    transition: 'all 0.15s ease'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: theme === 'dark' ? '#ffffff' : '#2d333a',
                marginBottom: '0.5rem'
              }}>
                Password
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{
                  position: 'absolute',
                  left: '1rem',
                  color: '#8e9297',
                  fontSize: '0.875rem',
                  zIndex: 1
                }}>
                  🔒
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Enter your password"
                  style={{
                    width: '100%',
                    padding: '1rem 3rem 1rem 2.5rem',
                    border: `1px solid ${theme === 'dark' ? '#404040' : '#e5e5e5'}`,
                    borderRadius: '0.5rem',
                    backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff',
                    color: theme === 'dark' ? '#ffffff' : '#2d333a',
                    fontSize: '1rem',
                    transition: 'all 0.15s ease'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '1rem',
                    background: 'none',
                    border: 'none',
                    color: '#8e9297',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    borderRadius: '0.25rem',
                    transition: 'color 0.15s ease'
                  }}
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1.5rem',
              flexWrap: 'wrap',
              gap: '0.5rem'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '0.875rem',
                color: '#6e7681'
              }}>
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  style={{ marginRight: '0.5rem' }}
                />
                Remember me
              </label>
              <button type="button" style={{
                color: '#274797',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
                transition: 'color 0.15s ease'
              }}>
                Forgot password?
              </button>
            </div>

            {error && (
              <div style={{
                padding: '0.75rem',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '0.5rem',
                color: '#dc2626',
                fontSize: '0.875rem',
                marginBottom: '1.5rem'
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '1rem 1.5rem',
                border: 'none',
                borderRadius: '0.5rem',
                backgroundColor: loading ? '#8e9297' : '#274797',
                color: 'white',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? (
                <>
                  <div className="spinner" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div style={{ textAlign: 'center' }}>
            <p style={{
              color: theme === 'dark' ? '#9ca3af' : '#6e7681',
              fontSize: '0.875rem'
            }}>
              Don't have an account?{' '}
              <Link to="/register" style={{
                color: '#274797',
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'color 0.15s ease'
              }}>
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default LoginPage;
